import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import styles from './DiamondGame.module.css';
import SettingsModal from '../components/modals/SettingsModal';
import diamondImage from '../assets/images/diamond2.png';

const DiamondGame = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showGameListModal, setShowGameListModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleGameList = () => {
    setShowGameListModal(true);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareModal(true);
    } catch (err) {
      alert('링크 복사에 실패했어요. 주소창의 URL을 직접 복사해주세요!');
      console.error('클립보드 복사 실패:', err);
    }
  };

  useEffect(() => {
    const loadUnityModule = async () => {
      try {
        // Unity 로더 스크립트만 로드
        const unityLoader = document.createElement('script');
        unityLoader.src = '/diamond-game/Build/PlinqerGameWebGl.loader.js';
        
        // 로더 스크립트가 로드되면 게임 초기화
        unityLoader.onload = () => {
          const canvas = document.querySelector("#unity-canvas");
          const config = {
            dataUrl: "/diamond-game/Build/PlinqerGameWebGl.data",
            frameworkUrl: "/diamond-game/Build/PlinqerGameWebGl.framework.js",
            codeUrl: "/diamond-game/Build/PlinqerGameWebGl.wasm",
            streamingAssetsUrl: "/diamond-game/StreamingAssets",
            companyName: "DefaultCompany",
            productName: "Diamond Game",
            productVersion: "1.0",
            // 추가 설정
            showBanner: false,
            backgroundColor: "#FFFFFF"
          };

          // Unity 인스턴스 생성 시도
          if (window.createUnityInstance) {
            window.createUnityInstance(canvas, config)
              .then(() => {
                setIsLoading(false);
                console.log('Unity game loaded successfully');
              })
              .catch(error => {
                console.error('Unity game load failed:', error);
                setIsLoading(false);
              });
          } else {
            console.error('createUnityInstance not found');
            setIsLoading(false);
          }
        };

        // 에러 처리
        unityLoader.onerror = (error) => {
          console.error('Unity loader script failed to load:', error);
          setIsLoading(false);
        };

        // 스크립트 추가
        document.body.appendChild(unityLoader);

      } catch (error) {
        console.error('Unity game initialization failed:', error);
        setIsLoading(false);
      }
    };

    loadUnityModule();

    // 클린업
    return () => {
      const scripts = document.querySelectorAll('script[src*="diamond-game"]');
      scripts.forEach(script => script.remove());
      
      // Unity 인스턴스 정리
      if (window.gameInstance) {
        window.gameInstance.Quit();
      }
    };
  }, []);

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.menuContainer}`)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.menuContainer}>
          <button 
            className={styles.menuButton}
            onClick={() => setShowMenu(!showMenu)}
          >
            <svg 
              className={styles.menuIcon} 
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
          
          {showMenu && (
            <div className={styles.menuDropdown}>
              <button 
                className={styles.menuItem}
                onClick={() => {
                  setShowMenu(false);
                  setShowGameListModal(true);
                }}
              >
                {t('gameList')}
              </button>
              <button 
                className={styles.menuItem}
                onClick={() => {
                  setShowMenu(false);
                  setShowRulesModal(true);
                }}
              >
                {t('games.diamond.rules.title')}
              </button>
              <button 
                className={styles.menuItem}
                onClick={() => {
                  setShowMenu(false);
                  setShowSettingsModal(true);
                }}
              >
                {t('settings')}
              </button>
            </div>
          )}
        </div>
      </div>
      {isLoading && (
        <div className={styles.loading}>
          <p>게임을 불러오는 중...</p>
        </div>
      )}
      <canvas 
        id="unity-canvas" 
        className={styles.unityCanvas}
        style={{ 
          visibility: isLoading ? 'hidden' : 'visible',
          width: '100%',
          height: '100%'
        }}
      />

      {showGameListModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{t('modals.gameList.message')}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowGameListModal(false)}>
                {t('modals.confirm.cancel')}
              </button>
              <button onClick={() => {
                setShowGameListModal(false);
                navigate('/');
              }}>
                {t('modals.confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRulesModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t('games.diamond.rules.title')}</h2>
            <p>{t('games.diamond.rules.description')}</p>
            <img 
              src={diamondImage} 
              alt="Diamond Game Rules" 
              className={styles.rulesImage}
            />
            <div className="modal-buttons">
              <button onClick={() => setShowRulesModal(false)}>
                {t('modals.confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettingsModal && (
        <SettingsModal
          onClose={() => setShowSettingsModal(false)}
          onContact={() => {
            setShowSettingsModal(false);
            setShowContactModal(true);
          }}
          onShare={handleShare}
        />
      )}

      {showContactModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{t('modals.contact.message')}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowContactModal(false)}>
                {t('modals.contact.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="modal-overlay">
          <div className="modal share-modal">
            <p>{t('modals.share.message')}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowShareModal(false)}>
                {t('modals.share.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiamondGame; 